import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, faqData } from '@/mocks/movies';

export default function About() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: '关于我们' },
  ];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 91精品视频在线播放',
    description: '了解91精品视频在线播放的品牌故事、核心优势与联系方式。',
    url: 'https://91jingpin.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '91精品视频在线播放',
      url: 'https://91jingpin.com/',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    description: siteInfo.description,
    url: 'https://91jingpin.com/',
    email: siteInfo.email,
    foundingDate: siteInfo.established,
    address: {
      '@type': 'PostalAddress',
      addressLocality: siteInfo.address,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteInfo.email,
      contactType: 'customer service',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <article className="mt-8">
                <section className="mb-12">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    关于我们
                  </h1>
                  <p className="text-sm text-foreground-500 mb-6">
                    成立于 {siteInfo.established}，致力于为用户提供最优质的影视推荐服务
                  </p>
                  <div className="rounded-lg overflow-hidden h-48 md:h-64 mb-6">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20cinema%20lobby%20with%20warm%20ambient%20lighting%2C%20elegant%20interior%20design%2C%20movie%20posters%20on%20walls%2C%20comfortable%20seating%20area%2C%20professional%20photography%2C%20inviting%20atmosphere%2C%20clean%20contemporary%20aesthetic&width=1200&height=500&seq=about-hero-cinema&orientation=landscape"
                      alt="关于我们"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-4">
                    品牌介绍
                  </h2>
                  <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                    <p>
                      91精品视频在线播放是一个专注于影视内容推荐与导航的平台。我们相信，好的影视作品能够丰富人们的精神世界，带来思考、感动与快乐。自{siteInfo.established}成立以来，我们始终坚持以用户需求为导向，精心筛选海量影视资源，为观众推荐真正值得观看的优质内容。
                    </p>
                    <p>
                      在这个信息爆炸的时代，面对影视平台上琳琅满目的作品，很多人陷入了"选择困难症"——不知道该看什么，往往浪费大量时间在翻找中。91精品视频在线播放的使命，就是帮助每一位用户高效找到适合自己的好电影、好剧集，让每一次观影都物有所值。
                    </p>
                    <p>
                      我们的编辑团队由一群热爱影视的资深影迷组成，他们对各类影片有着独到的见解和审美。每一部出现在推荐列表上的作品，都经过了团队的精挑细选和深度评估，确保推荐的影片在剧情、制作、表演等方面都具备过硬的质量。
                    </p>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-4">
                    核心优势
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      {
                        icon: 'ri-star-line',
                        title: '精选推荐',
                        desc: '每一部影片都经过编辑团队的严格筛选，告别低质内容',
                      },
                      {
                        icon: 'ri-refresh-line',
                        title: '每日更新',
                        desc: '紧跟影视动态，及时更新最新热门影片推荐',
                      },
                      {
                        icon: 'ri-menu-2-line',
                        title: '分类清晰',
                        desc: '多种分类方式帮助用户快速找到感兴趣的影片类型',
                      },
                      {
                        icon: 'ri-chat-3-line',
                        title: '专业评价',
                        desc: '每部推荐影片都配有专业的影评和详细剧情介绍',
                      },
                      {
                        icon: 'ri-4k-line',
                        title: '高清画质',
                        desc: '所有推荐资源均支持高清画质播放，享受视觉盛宴',
                      },
                      {
                        icon: 'ri-user-heart-line',
                        title: '用户至上',
                        desc: '持续优化推荐算法，为用户提供更精准的内容匹配',
                      },
                    ].map((item) => (
                      <div
                        key={item.title}
                        className="p-5 rounded-lg bg-background-100 border border-background-200"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                          <i className={`${item.icon} text-lg`}></i>
                        </div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                          {item.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-4">
                    用户口碑
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      {
                        name: '影视爱好者 小王',
                        comment: '自从用了91精品视频，再也不愁找不到好片看了！推荐质量很高，每次都能找到合口味的电影。',
                        rating: 5,
                      },
                      {
                        name: '追剧达人 Lisa',
                        comment: '分类很清晰，更新也很及时。特别是国产科幻板块，让我发现了不少宝藏作品。',
                        rating: 5,
                      },
                      {
                        name: '电影博主 老张',
                        comment: '作为一个资深影迷，这里的推荐确实有水平。不是随便凑数的，每部都是精心挑选的佳作。',
                        rating: 5,
                      },
                    ].map((review) => (
                      <div
                        key={review.name}
                        className="p-5 rounded-lg bg-background-100 border border-background-200"
                      >
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(review.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                          ))}
                        </div>
                        <p className="text-sm text-foreground-700 mb-3 leading-relaxed">
                          "{review.comment}"
                        </p>
                        <p className="text-xs text-foreground-500 font-medium">
                          — {review.name}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-4">
                    联系我们
                  </h2>
                  <div className="p-6 rounded-lg bg-background-100 border border-background-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                          <i className="ri-mail-line"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                          <p className="text-sm text-foreground-500">{siteInfo.email}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                          <i className="ri-map-pin-line"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">办公地址</p>
                          <p className="text-sm text-foreground-500">{siteInfo.address}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 pt-4 border-t border-background-200">
                      <p className="text-xs text-foreground-400">
                        如有任何建议或合作意向，欢迎通过以上方式联系我们。我们会在48小时内回复您的邮件。
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-4">
                    常见问题
                  </h2>
                  <div className="space-y-3">
                    {faqData.map((faq, index) => (
                      <details
                        key={index}
                        className="group rounded-lg bg-background-100 border border-background-200 overflow-hidden"
                      >
                        <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                          <span className="text-sm font-medium text-foreground-800 pr-4">
                            {faq.question}
                          </span>
                          <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                        </summary>
                        <div className="px-5 pb-4">
                          <p className="text-sm text-foreground-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              </article>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
}